/**
 * AdminReportsPanel — Super Admin triage view for /api/admin-reports.
 * Lists pending reports first, lets the admin mark Reviewed / Actioned /
 * Dismissed and add admin_notes. Also surfaces a per-silo report count
 * reputation signal (Bonus suggestion #3).
 */
import { useEffect, useState, useMemo } from "react";
import { Warning, CheckCircle, Trash, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_TINTS = {
  pending: "bg-[#FDF1ED] border-[#C16E5A]/30 text-[#C16E5A]",
  reviewed: "bg-[#FEF8E8] border-[#C19A2A]/30 text-[#A07820]",
  actioned: "bg-[#EAF2EC] border-[#4A5D4E]/30 text-[#4A5D4E]",
  dismissed: "bg-[#F0F1EE] border-[#6B6A68]/30 text-[#6B6A68]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminReportsPanel() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin-reports", { params: filter === "all" ? {} : { status: filter } });
      setReports(r.data.reports || []);
    } catch (e) {
      toast.error("Couldn't load reports.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  // Per-silo report count (reputation signal, super-admin-only).
  const siloCounts = useMemo(() => {
    const m = {};
    reports.filter((r) => r.silo_id).forEach((r) => {
      m[r.silo_id] = (m[r.silo_id] || 0) + 1;
    });
    return m;
  }, [reports]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin-reports/${id}`, { status });
      toast.success(`Marked ${status}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  return (
    <div data-testid="admin-reports-panel">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="font-serif-display text-2xl">Reports &amp; Contact</h2>
          <p className="text-xs text-[#6B6A68] mt-1">Silo reports, user reports, and contact-admin messages.</p>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {["pending", "reviewed", "actioned", "dismissed", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              data-testid={`reports-filter-${f}`}
              className={`px-2.5 py-1 rounded-md border hairline ${filter === f ? "bg-[#1C1B1A] text-white border-[#1C1B1A]" : "bg-white text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B6A68]">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="bg-white border hairline rounded-lg p-8 text-center text-sm text-[#6B6A68]">
          No reports in this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => {
            const tint = STATUS_TINTS[r.status] || STATUS_TINTS.pending;
            const repCount = r.silo_id ? siloCounts[r.silo_id] : null;
            return (
              <div key={r.id} className="bg-white border hairline rounded-lg p-4" data-testid={`admin-report-${r.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border ${tint}`}>{r.status}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{r.context}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{r.issue_type}</div>
                  <div className="ml-auto text-[10px] text-[#6B6A68] tabular-nums">{fmtDate(r.created_at)}</div>
                </div>

                <div className="mt-2 text-sm text-[#1C1B1A]">
                  {r.silo_label && (
                    <span className="text-[#4A5D4E] font-medium">Silo: {r.silo_label}</span>
                  )}
                  {repCount && repCount > 1 && (
                    <span className="ml-2 text-[10px] text-[#C16E5A]" title="Total reports for this silo across all statuses in current filter">
                      ⚠ {repCount} reports
                    </span>
                  )}
                  {r.target_user_label && (
                    <span className="text-[#4A5D4E] font-medium">User: {r.target_user_label}</span>
                  )}
                </div>

                <div className="mt-2 text-sm whitespace-pre-wrap">{r.description}</div>

                <div className="mt-2 text-[11px] text-[#6B6A68] flex items-center gap-3 flex-wrap">
                  <span>Reporter: {r.reporter_email || <em>anonymous</em>}</span>
                  <span>IP: {r.reporter_ip}</span>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {r.status !== "reviewed" && (
                    <button onClick={() => setStatus(r.id, "reviewed")}
                      data-testid={`report-mark-reviewed-${r.id}`}
                      className="text-xs px-2.5 py-1 rounded border hairline hover:border-[#A07820] hover:text-[#A07820] flex items-center gap-1">
                      <ChatCircleText size={12} /> Mark reviewed
                    </button>
                  )}
                  {r.status !== "actioned" && (
                    <button onClick={() => setStatus(r.id, "actioned")}
                      data-testid={`report-mark-actioned-${r.id}`}
                      className="text-xs px-2.5 py-1 rounded border hairline hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1">
                      <CheckCircle size={12} /> Mark actioned
                    </button>
                  )}
                  {r.status !== "dismissed" && (
                    <button onClick={() => setStatus(r.id, "dismissed")}
                      data-testid={`report-mark-dismissed-${r.id}`}
                      className="text-xs px-2.5 py-1 rounded border hairline hover:border-[#6B6A68] hover:text-[#6B6A68] flex items-center gap-1">
                      <Trash size={12} /> Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
